/**
 * Contact Us.
 *
 * Four ways to reach a person, all of them server-configured so the charity
 * can change who answers without shipping a build. A channel the operator has
 * left blank is not rendered — a button that opens nothing is worse than no
 * button, because it costs the donor a tap to discover the dead end.
 *
 * The full screen exists alongside the compact band on Home: the band is for
 * someone glancing, this is for someone who has decided to get in touch.
 */

import { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { colors, radius, sizing, space } from '../../theme';
import { ChevronIcon, GlyphCircle } from '../../components/common/Icon';
import { EmptyState, LoadingState } from '../../components/feedback/States';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { openDialler, openMail, openWebsite, openWhatsApp } from '../../core/linking/contact';
import { useAppConfig } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function ContactScreen({ navigation }: DonorScreenProps<'Contact'>) {
  const t = useT();
  const { data: config, isLoading } = useAppConfig();
  const support = config?.support;

  const onWhatsApp = useCallback(() => {
    if (support?.whatsapp) {
      void openWhatsApp(support.whatsapp, 'Hello, I have a question about Meal4ALL.');
    }
  }, [support?.whatsapp]);
  const onEmail = useCallback(() => {
    if (support?.email) void openMail(support.email, 'Meal4ALL enquiry');
  }, [support?.email]);
  const onCall = useCallback(() => {
    if (support?.phone) void openDialler(support.phone);
  }, [support?.phone]);
  const onWebsite = useCallback(() => {
    if (support?.website) void openWebsite(support.website);
  }, [support?.website]);

  const channels = [
    support?.whatsapp && {
      key: 'whatsapp',
      icon: 'chat',
      title: 'WhatsApp',
      value: support.whatsapp,
      hint: 'Opens WhatsApp',
      onPress: onWhatsApp,
    },
    support?.email && {
      key: 'email',
      icon: 'email',
      title: 'Email',
      value: support.email,
      hint: 'Opens your mail app',
      onPress: onEmail,
    },
    support?.phone && {
      key: 'call',
      icon: 'phone',
      title: 'Call',
      value: support.phone,
      hint: 'Opens your dialler',
      onPress: onCall,
    },
    support?.website && {
      key: 'website',
      icon: 'globe',
      title: 'Website',
      value: support.website,
      hint: 'Opens in your browser',
      onPress: onWebsite,
    },
  ].filter(Boolean) as {
    key: string;
    icon: string;
    title: string;
    value: string;
    hint: string;
    onPress: () => void;
  }[];

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-contact"
      title={t('Contact Us')}
      subtitle={t('We would rather hear from you than have you wonder.')}
    >

      {isLoading ? (
        <LoadingState label={t('Loading contact details')} />
      ) : channels.length === 0 ? (
        <EmptyState
          title={t('No contact details yet')}
          message={t('Support channels have not been set up. Please try again later.')}
        />
      ) : (
        <View style={styles.channels} testID="contact-channels">
          {channels.map((channel) => (
            <Pressable
              key={channel.key}
              onPress={channel.onPress}
              accessibilityRole="button"
              accessibilityLabel={`${t(channel.title)}: ${channel.value}`}
              accessibilityHint={t(channel.hint)}
              testID={`contact-row-${channel.key}`}
              style={({ pressed }) => [styles.row, pressed && styles.pressed]}
            >
              <GlyphCircle name={channel.icon} size={44} />
              <View style={styles.rowText}>
                <Text variant="cardTitle">{t(channel.title)}</Text>
                <Text variant="caption" tone="secondary">
                  {channel.value}
                </Text>
              </View>
              <ChevronIcon />
            </Pressable>
          ))}
        </View>
      )}

      {support?.hours ? (
        <View style={styles.hours} testID="contact-hours">
          <Text variant="caption" tone="tertiary" align="center">
            {support.hours}
          </Text>
        </View>
      ) : null}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  subtitle: {
    marginTop: space.xs,
    marginBottom: space.lg,
  },
  channels: {
    gap: space.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    minHeight: sizing.touchTarget,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border.default,
  },
  pressed: {
    opacity: 0.85,
  },
  rowText: {
    flex: 1,
    gap: space.xxs,
  },
  hours: {
    marginTop: space.xl,
  },
});
